import { useEffect, useState } from "react";
import api from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { Crown, Users, DollarSign, Trophy, Medal, Award } from "lucide-react";

const rankBadge = (rank) => {
  if (rank === 1) return { icon: Crown, cls: "text-[#D4AF37]", ring: "ring-[#D4AF37]" };
  if (rank === 2) return { icon: Trophy, cls: "text-slate-300", ring: "ring-slate-400" };
  if (rank === 3) return { icon: Medal, cls: "text-orange-400", ring: "ring-orange-500" };
  return { icon: Award, cls: "text-[#00E5FF]", ring: "ring-[#00E5FF]/50" };
};

export default function TopInvitersLeaderboard() {
  const { lang } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/leaderboard/inviters")
      .then((r) => { if (alive) setRows(r.data?.leaderboard || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <section className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="top-inviters-section">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-xs text-[#D4AF37] mb-4">
          <Crown className="w-3.5 h-3.5" /> {lang === "ar" ? "نظام إحالة مزدوج: 10% + 5%" : "Dual referral: 10% + 5%"}
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-3" data-testid="top-inviters-title">
          🏆 {lang === "ar" ? "أفضل 10 داعين هذا الشهر" : "Top 10 Inviters this Month"}
        </h2>
        <p className="text-sm text-[#8FA3BD] max-w-xl mx-auto">
          {lang === "ar"
            ? "ادعُ أصدقاءك — كلما زاد عدد المدعوين الذين يودعون، ارتفعت في الترتيب وارتفعت عمولتك."
            : "Invite friends — the more invitees deposit, the higher you climb and the more commission you earn."}
        </p>
      </div>

      {loading ? (
        <div className="text-center text-[#4A5970] py-10">...</div>
      ) : rows.length === 0 ? (
        <div className="bento-card p-8 text-center" data-testid="leaderboard-empty">
          <Crown className="w-10 h-10 text-[#D4AF37] mx-auto mb-3 opacity-60" />
          <p className="text-sm text-[#8FA3BD]">
            {lang === "ar" ? "كن أول من يظهر على لوحة المتصدّرين — ادعُ صديقاً اليوم!" : "Be the first on the leaderboard — invite a friend today!"}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="leaderboard-grid">
          {rows.map((r) => {
            const b = rankBadge(r.rank);
            const Icon = b.icon;
            return (
              <div key={r.rank}
                className={`bento-card p-5 relative ${r.rank <= 3 ? "border-[#D4AF37]/30" : ""} hover:border-[#00E5FF]/40 transition-all`}
                data-testid={`leaderboard-rank-${r.rank}`}>
                <div className={`absolute -top-3 -end-3 w-9 h-9 rounded-full bg-[#0F1F35] ring-2 ${b.ring} flex items-center justify-center text-xs font-bold ${b.cls}`}>
                  #{r.rank}
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#1A2D4D] overflow-hidden flex items-center justify-center border border-[#00E5FF]/20">
                    {r.picture ? (
                      <img src={r.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Icon className={`w-6 h-6 ${b.cls}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate" data-testid={`leaderboard-name-${r.rank}`}>{r.display_name}</div>
                    <div className="text-[10px] text-[#8FA3BD]">{lang === "ar" ? "داعٍ نشط" : "Active inviter"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-[#050E1A] border border-[#1A2D4D]">
                    <Users className="w-3.5 h-3.5 text-[#00E5FF] mx-auto mb-1" />
                    <div className="text-base font-bold text-[#00E5FF]" data-testid={`leaderboard-invites-${r.rank}`}>{r.successful_invites}</div>
                    <div className="text-[9px] text-[#8FA3BD]">{lang === "ar" ? "مدعو" : "invites"}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-[#050E1A] border border-[#1A2D4D]">
                    <DollarSign className="w-3.5 h-3.5 text-[#00E676] mx-auto mb-1" />
                    <div className="text-base font-bold text-[#00E676]" data-testid={`leaderboard-commission-${r.rank}`}>${r.total_commission.toFixed(2)}</div>
                    <div className="text-[9px] text-[#8FA3BD]">{lang === "ar" ? "عمولة" : "earned"}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
